<template>
  <div>
    <DefaultModal
      :title="modalTitle"
      ref="modal"
      @submit="save"
    >
      <template v-slot:content>
        <v-form ref="form">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="formData.pool_id"
                name="pool_id"
                :items="orderedPools"
                :label="$t('select_pool')"
                :rules="[v => !!v || $t('field_required')]"
                item-text="pool_title"
                item-value="pool_id"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="formData.status_id"
                name="status_id"
                :items="orderedStatuses"
                :label="$t('select_status')"
                :rules="[v => !!v || $t('field_required')]"
                item-text="status_title"
                item-value="status_id"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formData.pool_status_order"
                name="pool_status_order"
                type="number"
                :label="`${$t('order')}`"
                :rules="[v => v !== null && v !== undefined && v !== '' || $t('field_required')]"
                min="0"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formData.sla"
                name="sla"
                type="number"
                min="0"
                :label="`${$t('sla')}`"
                :rules="[v => v === null || v === '' || v >= 0 || $t('form_rules.zero_or_positive')]"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                v-model="formData.allow_sum_sla"
                name="allow_sum_sla"
                :label="`${$t('allow_sum_sla')}`"
                class="mt-1"
              ></v-checkbox>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="formData.form"
                name="form"
                :label="`${$t('form')}`"
                :rules="[
                  v => {
                    if (!v) return true;
                    try {
                      JSON.parse(v);
                      return true;
                    } catch (e) {
                      return $t('form_rules.invalid_json') || 'Invalid JSON format';
                    }
                  }
                ]"
                :rows="4"
                outlined
                dense
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </DefaultModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import DefaultModal from '@/components/DefaultModal.vue'
import JobActions from '@/store/job/actions-types'

export default {
  name: "JobPoolStatusDialog",

  components: {
    DefaultModal,
  },

  props: {
    jobPoolStatus: {
      type: Object,
      default: null
    },
    jobDefinition: {
      type: Object,
      required: true
    },
    pools: {
      type: Array,
      required: true
    },
    statuses: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      formData: {
        pool_id: null,
        status_id: null,
        pool_status_order: null,
        sla: null,
        allow_sum_sla: false,
        form: null,
      },
    }
  },

  computed: {
    modalTitle() {
      return this.jobPoolStatus
        ? this.$t('edit_job_definition')
        : this.$t('new_job_definition')
    },

    orderedPools() {
      return [...this.pools].sort((a, b) => {
        const titleA = a?.pool_title || ''
        const titleB = b?.pool_title || ''
        return titleA.localeCompare(titleB)
      })
    },

    orderedStatuses() {
      return [...this.statuses].sort((a, b) => {
        const titleA = a?.status_title || ''
        const titleB = b?.status_title || ''
        return titleA.localeCompare(titleB)
      })
    }
  },

  watch: {
    jobPoolStatus: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.formData = {
            pool_id: newVal.pool_id ?? null,
            status_id: newVal.status_id ?? null,
            pool_status_order: newVal.pool_status_order ?? null,
            sla: newVal.sla ?? null,
            allow_sum_sla: newVal.allow_sum_sla ?? false,
            form: newVal.form ?? null,
          }
        } else {
          this.formData = {
            pool_id: null,
            status_id: null,
            pool_status_order: null,
            sla: null,
            allow_sum_sla: false,
            form: null,
          }
        }
      }
    },
    'formData.status_id': {
      handler(newStatusId) {
        if (newStatusId) {
          const selectedStatus = this.statuses.find(status => status.status_id === newStatusId)
          const shouldFillSla = this.formData.sla === null || this.formData.sla === undefined
          if (selectedStatus && selectedStatus.status_sla !== undefined && shouldFillSla) {
            this.formData.sla = selectedStatus.status_sla
          }
        }
      }
    }
  },

  methods: {
    ...mapActions('job', [
      JobActions.CREATE_JOB_POOL_STATUS,
      JobActions.UPDATE_JOB_POOL_STATUS,
    ]),

    resetFormData() {
      this.formData = {
        pool_id: null,
        status_id: null,
        pool_status_order: null,
        sla: null,
        allow_sum_sla: false,
        form: null,
      }
    },

    openModal() {
      if (!this.jobPoolStatus) {
        this.resetFormData()
      }
      this.$refs.modal.openModal()
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    closeModal() {
      this.$refs.modal.closeModal()
      this.resetFormData()
    },

    save() {
      if (!this.$refs.form.validate()) {
        this.$refs.modal.submitting = false
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const payload = this.preparePayload(this.formData)

      let actionPromise

      if (this.jobPoolStatus) {
        const id = this.jobPoolStatus.job_pool_status_id
        actionPromise = this[JobActions.UPDATE_JOB_POOL_STATUS]({ id, data: payload })
      } else {
        actionPromise = this[JobActions.CREATE_JOB_POOL_STATUS](payload)
      }

      actionPromise
        .then(() => {
          const message = this.jobPoolStatus ? 'job_pool_status_updated' : 'job_pool_status_created'
          this.$emit('saved', message)
          this.$refs.modal.closeModal()
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
        .finally(() => {
          this.$refs.modal.submitting = false
        })
    },

    preparePayload(payload) {
      const job_definition_id = this.jobDefinition.job_definition_id
      const clonePayload = {...payload}

      this.$refs.form.inputs.forEach(input => {
        if(input.type === 'number') {
          clonePayload[input.$attrs.name] = Number(clonePayload[input.$attrs.name])
        }
      })

      return {job_definition_id, ...clonePayload}
    }
  }
}
</script>
